import React, { useEffect, useMemo, useState } from 'react';
import openaiService from '../services/openaiService.js';
import '../styles/AIAnalysisPanel.css';
import '../styles/InlineErrors.css';

const COMP_KEYS = ['I','II','III','IV','V'];
const TABS = ['Resumo', 'Competências', 'Problemas', 'Versão Corrigida'];

const AIAnalysisPanel = ({
  analysis,
  content,
  isImageAnalysis = false,
  originalContent,
  originalIsImage = false,
  onInlineErrorsUpdate,
  onRequestJumpToRange,
  onAnalysisComplete,
  title = 'Correção'
}) => {
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [mockMode, setMockMode] = useState(openaiService.mockMode || false);

  const [state, setState] = useState({ loading: false, error: '', result: analysis || null });
  const [activeTab, setActiveTab] = useState(TABS[0]);

  useEffect(() => {
    const key = openaiService.effectiveApiKey;
    const shouldAsk = openaiService.shouldAskKey;
    if (mockMode) {
      setShowApiKeyInput(false);
      return;
    }
    if (key) {
      setShowApiKeyInput(false);
    } else {
      setShowApiKeyInput(!shouldAsk);
    }
  }, [mockMode]);

  useEffect(() => {
    if (analysis) {
      setState({ loading: false, error: '', result: analysis });
      const errs = analysis.inlineMarks || analysis.inlineErrors || [];
      onInlineErrorsUpdate?.(errs);
      onAnalysisComplete?.(analysis);
    }
  }, [analysis, onInlineErrorsUpdate, onAnalysisComplete]);

  const compItems = useMemo(() => {
    const r = state.result || {};
    const scores = r.scores || {};
    return COMP_KEYS.map((k) => {
      const raw = scores[k] || {};
      const s = clampNum(raw.score, 0, 200);
      const pct = Math.max(0, Math.min(100, (s / 200) * 100));
      const notes = raw.justification || raw.notes || '';
      let cls = 'poor';
      if (pct >= 80) cls = 'excellent'; else if (pct >= 60) cls = 'good'; else if (pct >= 40) cls = 'average';
      return { key: k, score: s, pct, notes, cls };
    });
  }, [state.result]);

  // We include originalText + positions for better jump accuracy
  const inlineList = useMemo(() => {
    const r = state.result;
    if (!r) return [];
    const items = r.inlineMarks || r.inlineErrors || [];
    return items.map((it) => ({
      start: isNum(it.start) ? +it.start : null,
      end: isNum(it.end) ? +it.end : null,
      type: it.type || 'other',
      message: it.message || it.explanation || '',
      originalText: typeof it.originalText === 'string' ? it.originalText : '',
      correction: typeof it.correction === 'string' ? it.correction : ''
    }));
  }, [state.result]);

  const strengths = useMemo(() => {
    const arr = state.result?.strengths;
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((item) => typeof item === 'string' && item.trim())
      .map((item, idx) => ({ id: `strength-${idx}`, text: item.trim() }));
  }, [state.result]);

  const repertorio = useMemo(() => {
    const arr = state.result?.repertorioSociocultural;
    if (!Array.isArray(arr)) return [];
    return arr
      .map((item, idx) => ({
        id: `rep-${idx}`,
        reference: typeof item?.reference === 'string' ? item.reference.trim() : '',
        application: typeof item?.application === 'string' ? item.application.trim() : ''
      }))
      .filter((item) => item.reference && item.application);
  }, [state.result]);

  const roadmap = useMemo(() => {
    const arr = state.result?.roadmapTo1000;
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((item) => typeof item === 'string' && item.trim())
      .map((item, idx) => ({ id: `roadmap-${idx}`, text: item.trim() }));
  }, [state.result]);

  const exemplarSentences = useMemo(() => {
    const arr = state.result?.exemplarSentences;
    if (!Array.isArray(arr)) return [];
    return arr
      .map((item, idx) => ({
        id: `exemplar-${idx}`,
        excerpt: typeof item?.excerpt === 'string' ? item.excerpt.trim() : '',
        why: typeof item?.whyItWorks === 'string' ? item.whyItWorks.trim() : ''
      }))
      .filter((item) => item.excerpt && item.why);
  }, [state.result]);

  const paragraphFeedback = useMemo(() => {
    const arr = state.result?.paragraphFeedback;
    if (!Array.isArray(arr)) return [];
    return arr
      .map((item, idx) => ({
        id: `pf-${idx}`,
        paragraph: Number.isFinite(item?.paragraph) ? item.paragraph : idx + 1,
        summary: typeof item?.summary === 'string' ? item.summary.trim() : '',
        strength: typeof item?.strength === 'string' ? item.strength.trim() : '',
        improvement: typeof item?.improvement === 'string' ? item.improvement.trim() : '',
        snippet: typeof item?.snippet === 'string' ? item.snippet.trim() : ''
      }))
      .filter((item) => item.summary || item.improvement)
      .sort((a, b) => a.paragraph - b.paragraph);
  }, [state.result]);

  const hasGuidedSections = strengths.length > 0 || repertorio.length > 0 || roadmap.length > 0 || exemplarSentences.length > 0;

  const handleAnalyze = async () => {
    if (state.loading) return;

    if (!content) {
      setState((s) => ({ ...s, error: 'Nenhum conteúdo para analisar.' }));
      return;
    }

    if (!mockMode && !openaiService.effectiveApiKey) {
      setState((s) => ({ ...s, error: 'OPENAI_API_KEY ausente. Forneça a chave pelo campo ou via env.' }));
      if (!openaiService.shouldAskKey) setShowApiKeyInput(false);
      else setShowApiKeyInput(true);
      return;
    }

    setState({ loading: true, error: '', result: null });

    try {
      let res;
      if (isImageAnalysis) {
        res = await openaiService.analyzeEssay('', () => {}, content);
      } else {
        const d = document.createElement('div');
        d.innerHTML = content;
        const plainText = d.textContent || d.innerText || '';
        if (plainText.trim().length < 50) throw new Error('Texto curto demais para análise.');
        res = await openaiService.analyzeEssay(plainText, () => {});
      }

      const parsed = openaiService.parseAnalysisResponse(res.analysis);
      setState({ loading: false, error: '', result: parsed });

      const inlineForEditor = parsed.inlineMarks || parsed.inlineErrors || [];
      onInlineErrorsUpdate?.(inlineForEditor);
      onAnalysisComplete?.(parsed);
    } catch (err) {
      setState({ loading: false, error: err.message || 'Falha na análise.', result: null });
    }
  };

  const handleSaveKey = (e) => {
    e.preventDefault();
    const k = apiKey.trim();
    if (!k) return;
    try { window.localStorage.setItem('OPENAI_API_KEY', k); } catch {}
    openaiService.setApiKey(k);
    setShowApiKeyInput(false);
  };

  const renderOriginalSubmission = () => {
    const showImg = originalIsImage || (analysis ? originalIsImage : isImageAnalysis);
    const srcOrHtml = analysis ? (originalContent || '') : (content || '');
    return (
      <div className="original-submission">
        <h3>📝 Submissão original</h3>
        {showImg ? (
          <div className="original-image-wrap">
            <img src={srcOrHtml} alt="Submissão" />
          </div>
        ) : (
          <div className="original-text" dangerouslySetInnerHTML={{ __html: srcOrHtml }} />
        )}
      </div>
    );
  };

  return (
    <section className="ai-analysis-panel">
      <div className="ai-header">
        <h2>🤖 {title}</h2>
        {state.loading && <span className="badge">Analisando...</span>}
        {state.error && <span className="badge error">{state.error}</span>}
      </div>

      {(originalContent || (!analysis && content)) && renderOriginalSubmission()}

      {showApiKeyInput && !mockMode && (
        <div className="api-key-section">
          <h4>OpenAI API Key</h4>
          <p>Defina a chave aqui (ou via .env com REACT_APP_OPENAI_API_KEY/OPENAI_API_KEY).</p>
          <form onSubmit={handleSaveKey} className="api-key-form">
            <div className="api-key-input-group">
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk-..."
                className="api-key-input"
              />
              <button type="submit" className="api-key-submit" disabled={!apiKey.trim()}>
                Salvar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* {!analysis && (
        <div className="analysis-actions">

          <label className="mock-mode-toggle" style={{ marginLeft: 12 }}>
            <input
              type="checkbox"
              checked={mockMode}
              onChange={(e) => {
                setMockMode(e.target.checked);
                openaiService.setMockMode(e.target.checked);
                if (e.target.checked) setShowApiKeyInput(false);
              }}
            />
            <span style={{ marginLeft: 6 }}>🧪 Mock Mode</span>
          </label>
        </div>
      )} */}

      {state.result && (
        <>
          <div className="tabs">
            {TABS.map((tab) => (
              <button
                key={tab}
                className={`tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="analysis-results">
            {activeTab === 'Resumo' && (
              <div className="feedback">
                <div className="score-row">
                  <div className="total-score">
                    <div className="score">{clampNum(state.result.totalScore, 0, 1000)}</div>
                    <div className="label">/1000</div>
                  </div>
                  <div>
                    <h3>Resumo</h3>
                    <p>{state.result.feedbackSummary || '—'}</p>
                  </div>
                </div>

                {hasGuidedSections && (
                  <div className="guided-sections">
                    {strengths.length > 0 && (
                      <div className="feedback-section strengths-section">
                        <h4>Principais pontos fortes</h4>
                        <ul className="strengths-list">
                          {strengths.map((item) => (
                            <li key={item.id}>{item.text}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {exemplarSentences.length > 0 && (
                      <div className="feedback-section exemplar-section">
                        <h4>Trechos exemplares do texto</h4>
                        <div className="exemplar-grid">
                          {exemplarSentences.map((item) => (
                            <article key={item.id} className="exemplar-card">
                              <blockquote>“{item.excerpt}”</blockquote>
                              <p className="exemplar-reason">{item.why}</p>
                            </article>
                          ))}
                        </div>
                      </div>
                    )}

                    {repertorio.length > 0 && (
                      <div className="feedback-section repertorio-section">
                        <h4>Repertórios socioculturais sugeridos</h4>
                        <div className="repertorio-grid">
                          {repertorio.map((item) => (
                            <div key={item.id} className="repertorio-card">
                              <strong>{item.reference}</strong>
                              <p>{item.application}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {roadmap.length > 0 && (
                      <div className="feedback-section roadmap-section">
                        <h4>Plano de ação rumo à nota 1000</h4>
                        <ol className="roadmap-list">
                          {roadmap.map((item, idx) => (
                            <li key={item.id}>
                              <span className="step-index">Etapa {idx + 1}</span>
                              <span className="step-text">{item.text}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                )}

                {paragraphFeedback.length > 0 && (
                  <div className="paragraph-feedback">
                    <div className="section-header"><h4>Feedback por parágrafo</h4></div>
                    <div className="paragraph-grid">
                      {paragraphFeedback.map((item) => (
                        <article key={item.id} className="paragraph-card">
                          <div className="paragraph-meta">Parágrafo {item.paragraph}</div>
                          <p className="paragraph-summary">{item.summary}</p>
                          {item.strength && (
                            <p className="paragraph-strength">
                              <strong>Ponto forte:</strong> {item.strength}
                            </p>
                          )}
                          {item.improvement && (
                            <p className="paragraph-improvement">
                              <strong>Melhorar:</strong> {item.improvement}
                            </p>
                          )}
                          {item.snippet && <blockquote>“{item.snippet}”</blockquote>}
                        </article>
                      ))}
                    </div>
                  </div>
                )}

                {inlineList.length > 0 && (
                  <div className="inline-errors-section">
                    <div className="section-header"><h4>Marcações inline ({inlineList.length})</h4></div>
                    <div className="inline-errors-list">
                      {inlineList.map((err, idx) => (
                        <div key={idx} className={`inline-error-item error-type-${(err.type || 'other')}`}>
                          <div className="error-header">
                            <span className="error-type">{labelFor(err.type)}</span>
                            <button
                              className="goto-link"
                              onClick={() =>
                                onRequestJumpToRange?.({
                                  start: err.start,
                                  end: err.end,
                                  originalText: err.originalText || ''
                                })
                              }
                              title="Ir ao trecho no texto"
                            >
                              Ir ao trecho ↘
                            </button>
                          </div>
                          <div className="error-content">
                            {err.originalText && <div className="error-original"><strong>Original:</strong> “{err.originalText}”</div>}
                            {err.correction && <div className="error-correction"><strong>Sugerido:</strong> “{err.correction}”</div>}
                            <div className="error-explanation">{err.message}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'Competências' && (
              <div className="score-row">
                <div className="total-score">
                  <div className="score">{clampNum(state.result.totalScore, 0, 1000)}</div>
                  <div className="label">/1000</div>
                </div>

                <div className="comp-grid">
                  {compItems.map(({ key, score, pct, notes, cls }) => (
                    <div key={key} className="comp-card">
                      <div className="comp-head">
                        <div className="comp-name">Comp. {key}</div>
                        <div className={`comp-score ${cls}`}>{score}/200</div>
                      </div>
                      <div className="competency-bar"><div className={`competency-fill ${cls}`} style={{ width: `${pct}%` }} /></div>
                      {notes && <div className="comp-notes">{notes}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'Problemas' && (
              <div className="problems-suggestions">
                {Array.isArray(state.result.problems) && state.result.problems.length > 0 && (
                  <div className="problems-section">
                    <h4>Problemas identificados</h4>
                    <ul className="problems-list">
                      {state.result.problems.map((p, i) => <li key={i} className="problem-item">{p}</li>)}
                    </ul>
                  </div>
                )}
                {Array.isArray(state.result.suggestions) && state.result.suggestions.length > 0 && (
                  <div className="suggestions-section">
                    <h4>Sugestões</h4>
                    <ul className="suggestions-list">
                      {state.result.suggestions.map((s, i) => <li key={i} className="suggestion-item">{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'Versão Corrigida' && state.result.correctedVersion && (
              <div className="corrected-version">
                <h4>Versão corrigida</h4>
                <div className="corrected-text">{state.result.correctedVersion}</div>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default AIAnalysisPanel;

/* helpers */
function isNum(v) { return Number.isFinite(+v); }
function clampNum(n, lo, hi) { const x = Number.isFinite(+n) ? +n : 0; return Math.max(lo, Math.min(hi, x)); }
function labelFor(type) {
  const map = {
    grammar: 'Gramática', spelling: 'Ortografia', punctuation: 'Pontuação', agreement: 'Concordância',
    cohesion: 'Coesão', content: 'Conteúdo', register: 'Registro', structure: 'Estrutura',
    argument: 'Argumentação', style: 'Estilo', other: 'Outro'
  };
  return map[type] || type || 'Outro';
}
